/**
 * GiftCardsPage.tsx
 *
 * Full gift card management page for staff.
 * Lists gift cards with search, sidebar filters, stats, and per-row actions.
 * Supports issuing new cards (via IssueGiftCardModal) and voiding active cards.
 * Cards sold online show their buyer, recipient and claim status; "Issued To"
 * links to the owner's profile, and unclaimed online codes stay masked.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { usePageContext } from '@/hooks/usePageContext';
import { useAuth } from '@/hooks/useAuth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';


import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { PatientLink } from '@/components/ui/PatientLink';


import {
  isDateRange,
  isSelect,
  type FilterConfig,
  type FilterOption,
  type FilterValue,
} from '@/types/filters.types';
import { useLocalDashboardFilters } from '@/pages/dashboard/dashboardFilters';
import { buildApiParams, extractSearch, FILTER_KEYS } from '@/lib/filterRegistry';
import { useDebounce } from '@/hooks/useDebounce';
// Shared formatter — the page's own helper was a bare toFixed(2), so a
// five-figure outstanding balance rendered without thousands separators.
import { formatCurrency } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import {
  listGiftCards,
  listGiftCardRedemptions,
  redemptionsToCsv,
  voidGiftCard,
  getGiftCardStats,
  type GiftCard,
  type GiftCardListParams,
  type GiftCardRedemption,
  type PaginatedGiftCards,
} from '@/services/gift-cards.service';
import IssueGiftCardModal from './IssueGiftCardModal';
import GiftCardDetailModal from './GiftCardDetailModal';

// ─── Filter configs ──────────────────────────────────────────────────────────

// `multi: true` renders the standard inline checklist with the +/- negate
// toggle, the same control the rest of the site's list filters use.
const FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search gift cards...' },
  { key: 'daterange', label: 'Purchase Date', type: 'daterange' },
  { key: 'status', label: 'Status', type: 'select', multi: true, allLabel: 'All statuses' },
  { key: FILTER_KEYS.ONLINE_SALE, label: 'Online Sale', type: 'select', multi: true, allLabel: 'All cards' },
];

// Values must match ListFilterService::GIFT_CARD_STATUS_CASE, which resolves
// them from the card's balance / redeemed / expiry the same way the row badge
// does — including Redeemed, which the list showed but you couldn't filter on.
const STATUS_FILTER_OPTIONS: FilterOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'redeemed', label: 'Redeemed' },
  { value: 'voided', label: 'Voided' },
  { value: 'expired', label: 'Expired' },
];

// Values must match ListFilterService::GIFT_CARD_ONLINE_SALE_CASE, which
// decides claimed the same way the Online Sale badge does (an owner, or a
// claimant). Picking either shows only cards sold online; excluding one still
// lists the desk cards.
const ONLINE_SALE_FILTER_OPTIONS: FilterOption[] = [
  { value: 'unclaimed', label: 'Unclaimed' },
  { value: 'claimed', label: 'Claimed' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString();
}

function isExpired(card: GiftCard): boolean {
  if (!card.expiresDate) return false;
  return new Date(card.expiresDate) < new Date();
}

/** Sold online and not yet claimed: whoever holds the code can still claim it. */
function isUnclaimedOnline(card: GiftCard): boolean {
  return !!card.isOnline && !card.claimedPatientId && !card.issuedToPatientId;
}

/**
 * An unclaimed online code stays masked in the list (KTD19). The API already
 * masks it for users without the Gift Credit permission; users with it reveal
 * it in the detail modal, where each view is logged.
 */
function listCode(card: GiftCard): string {
  const code = card.serialNumber;
  if (!isUnclaimedOnline(card) || code.includes('*')) return code;
  return code.length <= 4 ? '*'.repeat(code.length) : '*'.repeat(code.length - 4) + code.slice(-4);
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function GiftCardsPage() {
  const queryClient = useQueryClient();
  const updateAIContext = usePageContext('Gift Cards');
  const { user, hasPermission } = useAuth();
  const canManageGiftCards = (user?.userLevel ?? 0) > 3 || hasPermission(14);

  // ── View: card inventory vs. redemption ledger ──────────────────────────
  const [view, setView] = useState<'cards' | 'redemptions'>('cards');
  const [exporting, setExporting] = useState(false);

  // ── Filter state ────────────────────────────────────────────────────────
  // Shared, localStorage-persisted filter state with negation support — the
  // same hook + payload builder every other list page uses.
  const filterState = useLocalDashboardFilters(FILTER_CONFIGS, 'gift-cards');
  const search = extractSearch(filterState.values);
  const debouncedSearch = useDebounce(search, 300);

  const [page, setPage] = useState(1);
  const perPage = 100;
  // Sorting is server-side: the list is paginated, and DataTable's client-side
  // sort is a no-op for columns that use `render` (all of these do).
  const [sortBy, setSortBy] = useState<string>('purchaseDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [copiedSerial, setCopiedSerial] = useState<string | null>(null);

  // ── Modal state ─────────────────────────────────────────────────────────
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [detailCardId, setDetailCardId] = useState<number | null>(null);
  const [voidTarget, setVoidTarget] = useState<GiftCard | null>(null);
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);

  // ── Query params ─────────────────────────────────────────────────────────
  // The sidebar commits search on every keystroke, so the debounced value is
  // substituted in here rather than debouncing around it: the params content
  // then only changes when the debounce fires, and react-query hashes the key
  // structurally, so typing doesn't refetch per character.
  const params = useMemo<GiftCardListParams>(
    () => buildApiParams(
      { ...filterState.values, search: debouncedSearch ? [debouncedSearch] : [] },
      filterState.negations,
      { page, perPage, sortBy, sortDir },
    ) as GiftCardListParams,
    [filterState.values, filterState.negations, debouncedSearch, page, perPage, sortBy, sortDir],
  );

  const { data, isLoading } = useQuery({
    queryKey: ['gift-cards', params],
    queryFn: () => listGiftCards(params),
    placeholderData: (prev: PaginatedGiftCards | undefined) => prev,
  });

  const { data: gcStats, error: gcStatsError } = useQuery({
    queryKey: ['gift-cards-stats'],
    queryFn: () => getGiftCardStats(),
    staleTime: 60_000,
  });

  // ── Redemption ledger ────────────────────────────────────────────────────
  // Takes explicit params rather than filtersArray: its own date filter is on
  // the redemption date, not the card's purchase date, and card status doesn't
  // apply to a redemption row.
  const dateRange = isDateRange(filterState.values.daterange)
    ? filterState.values.daterange
    : { from: '', to: '' };

  const redemptionParams = useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
      dateFrom: dateRange.from || undefined,
      dateTo: dateRange.to || undefined,
      page,
      perPage,
    }),
    [debouncedSearch, dateRange.from, dateRange.to, page, perPage],
  );

  const {
    data: redemptionData,
    isLoading: redemptionsLoading,
    error: redemptionsError,
  } = useQuery({
    queryKey: ['gift-card-redemptions', redemptionParams],
    queryFn: () => listGiftCardRedemptions(redemptionParams),
    enabled: view === 'redemptions',
  });

  // Export honours the active filters but ignores pagination — accounting
  // reconciles the whole filtered period, not the page they happen to be on.
  const handleExportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const all = await listGiftCardRedemptions({ ...redemptionParams, page: 1, perPage: 1000 });
      const csv = redemptionsToCsv(all.data);
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `gift-card-redemptions-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }, [redemptionParams]);

  // ── Void mutation ─────────────────────────────────────────────────────
  const voidMutation = useMutation({
    mutationFn: (id: number) => voidGiftCard(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gift-cards'] });
      setVoidConfirmOpen(false);
      setVoidTarget(null);
    },
  });

  // ── Derived stats ─────────────────────────────────────────────────────
  const cards = data?.data ?? [];
  const totalActive  = gcStats?.active       ?? 0;
  const totalBalance = gcStats?.totalBalance ?? 0;
  const totalIssued  = gcStats?.total        ?? data?.meta.total ?? 0;
  const totalRedeemed = gcStats?.redeemed    ?? 0;

  // ── AI page context ────────────────────────────────────────────────────────
  const statusValues = isSelect(filterState.values.status) ? filterState.values.status : [];
  const onlineSaleFilter = filterState.values[FILTER_KEYS.ONLINE_SALE];
  const onlineSaleValues = isSelect(onlineSaleFilter) ? onlineSaleFilter : [];

  useEffect(() => {
    const filterParts: string[] = [];
    if (statusValues.length > 0) {
      filterParts.push(`Status${filterState.negations.status ? ' (excluding)' : ''}: ${statusValues.join(', ')}`);
    }
    if (onlineSaleValues.length > 0) {
      filterParts.push(`Online sale${filterState.negations[FILTER_KEYS.ONLINE_SALE] ? ' (excluding)' : ''}: ${onlineSaleValues.join(', ')}`);
    }
    if (search) filterParts.push(`Search: "${search}"`);
    if (dateRange.from || dateRange.to) {
      filterParts.push(`Purchased: ${dateRange.from || '...'} to ${dateRange.to || '...'}`);
    }
    updateAIContext({
      filters: filterParts.length > 0 ? filterParts.join(', ') : 'No filters applied',
      dataSummary: `${totalIssued} issued, ${totalActive} active, ${formatCurrency(totalBalance)} outstanding`,
    });
  }, [
    search,
    statusValues,
    filterState.negations.status,
    onlineSaleValues,
    filterState.negations[FILTER_KEYS.ONLINE_SALE],
    dateRange.from,
    dateRange.to,
    totalIssued,
    totalActive,
    totalBalance,
    updateAIContext,
  ]);

  // ── Sidebar filter wiring ─────────────────────────────────────────────
  const sidebarOptions = useMemo<Record<string, FilterOption[]>>(
    () => ({
      status: STATUS_FILTER_OPTIONS,
      [FILTER_KEYS.ONLINE_SALE]: ONLINE_SALE_FILTER_OPTIONS,
    }),
    [],
  );

  const handleSidebarChange = useCallback((filterKey: string, value: FilterValue) => {
    filterState.setFilter(filterKey, value);
    setPage(1);
  }, [filterState]);

  const handleSidebarClear = useCallback((filterKey: string) => {
    filterState.clearFilter(filterKey);
    setPage(1);
  }, [filterState]);

  const handleSidebarNegate = useCallback((filterKey: string, negate: boolean) => {
    filterState.setNegation(filterKey, negate);
    setPage(1);
  }, [filterState]);

  // ── Handlers ──────────────────────────────────────────────────────────
  function handleCopySerial(serial: string) {
    void navigator.clipboard.writeText(serial).then(() => {
      setCopiedSerial(serial);
      setTimeout(() => setCopiedSerial(null), 2000);
    });
  }

  const handleSortChange = useCallback((key: string, direction: 'asc' | 'desc') => {
    setSortBy(key);
    setSortDir(direction);
    setPage(1);
  }, []);

  function handleVoidConfirm() {
    if (!voidTarget) return;
    voidMutation.mutate(voidTarget.giftCardId);
  }

  // ── DataTable columns ────────────────────────────────────────────────
  const columns = useMemo<DataTableColumn<GiftCard>[]>(() => [
    {
      key: 'serialNumber',
      header: 'Serial Number',
      sortable: true,
      render: (card) => (
        <div className="flex items-center gap-2">
          <span className="font-mono text-slate-800 font-medium">{listCode(card)}</span>
          {!isUnclaimedOnline(card) && (
            <button
              onClick={(e) => { e.stopPropagation(); handleCopySerial(card.serialNumber); }}
              className="text-slate-400 hover:text-indigo-600 transition-colors"
              title="Copy serial number"
            >
              {copiedSerial === card.serialNumber ? (
                <span className="text-xs text-green-600 font-medium">Copied</span>
              ) : (
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          )}
        </div>
      ),
    },
    {
      key: 'originalValue',
      header: 'Original Value',
      align: 'right' as const,
      sortable: true,
      render: (card) => <span className="text-slate-700">{formatCurrency(card.originalValue)}</span>,
    },
    {
      key: 'currentBalance',
      header: 'Current Balance',
      align: 'right' as const,
      sortable: true,
      render: (card) => <span className="font-semibold text-slate-900">{formatCurrency(card.currentBalance)}</span>,
    },
    {
      key: 'issuedToPatientName',
      header: 'Issued To',
      sortable: true,
      // The card's owner, linked to their profile. For an online card that is
      // the recipient once they claim it; until then nobody owns it.
      render: (card) => {
        if (!card.issuedToPatientId) {
          return <span className="text-slate-400">—</span>;
        }
        const name = card.issuedToPatientName ?? `Patient #${card.issuedToPatientId}`;
        return (
          <PatientLink
            patientId={card.issuedToPatientId}
            patientName={name}
            onClick={(e) => e.stopPropagation()}
            className="text-blue-600 hover:underline"
          >
            {name}
          </PatientLink>
        );
      },
    },
    {
      key: 'online',
      header: 'Online Sale',
      // Buyer → recipient and the claim, for cards sold through API v4.
      render: (card) => {
        if (!card.isOnline) return <span className="text-slate-400">—</span>;
        const recipient = card.sendToSelf
          ? 'self'
          : (card.recipientName ?? card.recipientEmail ?? '—');
        return (
          <div className="space-y-0.5">
            <div className="text-xs text-slate-600">
              <span className="font-medium text-slate-700">{card.buyerName ?? '—'}</span>
              {' → '}
              <span className="font-medium text-slate-700">{recipient}</span>
            </div>
            {isUnclaimedOnline(card) ? (
              <Badge variant="warning">Unclaimed</Badge>
            ) : (
              <Badge variant="info">Claimed</Badge>
            )}
          </div>
        );
      },
    },
    {
      key: 'purchaseDate',
      header: 'Purchase Date',
      sortable: true,
      render: (card) => <span className="text-slate-600">{formatDate(card.purchaseDate)}</span>,
    },
    {
      key: 'expiresDate',
      header: 'Expires',
      sortable: true,
      render: (card) => {
        const expired = isExpired(card);
        return (
          <span className={expired ? 'text-red-600 font-semibold' : 'text-slate-600'}>
            {formatDate(card.expiresDate)}
            {expired && (
              <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">
                EXPIRED
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: 'active',
      header: 'Status',
      sortable: true,
      render: (card) => {
        const fullyRedeemed = card.currentBalance === 0 && (card.redeemedAmount ?? 0) > 0;
        if (fullyRedeemed) {
          return <Badge variant="neutral" dot>Redeemed</Badge>;
        }
        if (!card.active) {
          return <Badge variant="error" dot>Voided</Badge>;
        }
        const expired = isExpired(card);
        if (expired) {
          return <Badge variant="warning" dot>Expired</Badge>;
        }
        return <Badge variant="success" dot>Active</Badge>;
      },
    },
  ], [copiedSerial]);

  const redemptionColumns = useMemo<DataTableColumn<GiftCardRedemption>[]>(() => [
    {
      key: 'serialNumber',
      header: 'Serial',
      render: (r) => <span className="font-mono font-medium text-slate-800">{r.serialNumber ?? '—'}</span>,
    },
    {
      key: 'redeemedDate',
      header: 'Date',
      render: (r) => <span className="text-slate-600">{formatDate(r.redeemedDate)}</span>,
    },
    {
      key: 'patientName',
      header: 'Patient',
      render: (r) => <span className="text-slate-700">{r.patientName ?? '—'}</span>,
    },
    {
      key: 'redeemedAmount',
      header: 'Redeemed',
      align: 'right' as const,
      render: (r) => <span className="font-semibold text-slate-900">{formatCurrency(r.redeemedAmount)}</span>,
    },
    {
      key: 'appliedTo',
      header: 'Applied To',
      render: (r) => (
        <div>
          <span className="text-slate-700">{r.appliedTo ?? '—'}</span>
          {!r.linked && (
            <span className="ml-2 inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
              UNLINKED
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'remainingBalance',
      header: 'Remaining Balance',
      align: 'right' as const,
      render: (r) => <span className="text-slate-700">{formatCurrency(r.remainingBalance)}</span>,
    },
    {
      key: 'facilityName',
      header: 'Facility',
      render: (r) => <span className="text-slate-600">{r.facilityName ?? '—'}</span>,
    },
  ], []);

  const redemptions = redemptionData?.data ?? [];


  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <ListPageLayout
        filterSidebar={(
          <FilterSidebar
            filters={FILTER_CONFIGS}
            values={filterState.values}
            options={sidebarOptions}
            onChange={handleSidebarChange}
            onClear={handleSidebarClear}
            negations={filterState.negations}
            onNegateChange={handleSidebarNegate}
            storageKey="gift-cards"
            showAIAssistant={false}
          />
        )}
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="shrink-0 space-y-6 p-6">
            <PageHeader
              variant="compact"
              title="Gift Cards"
              actions={(
                <div className="flex items-center gap-2">
                  {view === 'redemptions' && (
                    <Button variant="secondary" onClick={handleExportCsv} disabled={exporting}>
                      {exporting ? 'Exporting…' : 'Export CSV'}
                    </Button>
                  )}
                  {canManageGiftCards && (
                    <Button onClick={() => setIssueModalOpen(true)}>
                      + Issue Gift Card
                    </Button>
                  )}
                </div>
              )}
            />

            {/* Say so when the figures failed to load, rather than presenting
                zeros as though they were the real counts. */}
            {gcStatsError ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
                Summary figures unavailable — {getErrorMessage(gcStatsError, 'Unknown error')}
              </div>
            ) : null}

            <MetricCardGrid columns={4}>
              <StatCard label="Active Cards" value={totalActive} />
              <StatCard
                label="Outstanding Balance"
                value={formatCurrency(totalBalance)}
                subtitle="Redeemable value, excl. expired"
              />
              <StatCard label="Total Issued" value={totalIssued} />
              <StatCard
                label="Redeemed"
                value={totalRedeemed}
                subtitle="Fully spent cards"
              />
            </MetricCardGrid>

            {/* Card inventory vs. the redemption ledger — the latter is what
                reconciles stored value spent against revenue earned. */}
            <div className="inline-flex rounded-lg bg-slate-100 p-1">
              {([
                { key: 'cards' as const, label: 'Cards' },
                { key: 'redemptions' as const, label: 'Redemption Activity' },
              ]).map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => { setView(tab.key); setPage(1); }}
                  aria-pressed={view === tab.key}
                  className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
                    view === tab.key
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            {view === 'cards' ? (
              <DataTable<GiftCard>
                data={cards}
                columns={columns}
                keyField="giftCardId"
                loading={isLoading}
                manualSort
                sortKey={sortBy}
                sortDir={sortDir}
                onSortChange={handleSortChange}
                onRowClick={(card) => setDetailCardId(card.giftCardId)}
                page={data?.meta.page ?? page}
                pageSize={perPage}
                total={data?.meta.total ?? 0}
                onPageChange={(p) => setPage(p)}
                emptyTitle="No gift cards found"
                emptyDescription="Try adjusting your filters."
              />
            ) : redemptionsError ? (
              // Never fall through to the empty state on failure: a 404 or a
              // query error is indistinguishable from "no redemptions" once the
              // table renders, which is exactly how the broken stats endpoint
              // stayed invisible behind four zeroed tiles.
              <div className="m-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                <p className="font-medium">Could not load redemption activity.</p>
                <p className="mt-1 text-xs">{getErrorMessage(redemptionsError, 'Unknown error')}</p>
              </div>
            ) : (
              <DataTable<GiftCardRedemption>
                data={redemptions}
                columns={redemptionColumns}
                keyField="giftCardLogID"
                loading={redemptionsLoading}
                onRowClick={(r) => setDetailCardId(r.giftCardId)}
                page={redemptionData?.meta.page ?? page}
                pageSize={perPage}
                total={redemptionData?.meta.total ?? 0}
                onPageChange={(p) => setPage(p)}
                emptyTitle="No redemptions found"
                emptyDescription="Try adjusting your filters."
              />
            )}
          </div>
        </div>
      </ListPageLayout>

      {/* ── Issue modal ── */}
      <IssueGiftCardModal
        open={issueModalOpen}
        onClose={() => setIssueModalOpen(false)}
        onIssued={() => {
          void queryClient.invalidateQueries({ queryKey: ['gift-cards'] });
          setIssueModalOpen(false);
        }}
      />

      {/* ── Detail / transact modal ── */}
      {detailCardId !== null && (
        <GiftCardDetailModal
          open={detailCardId !== null}
          onClose={() => setDetailCardId(null)}
          onChanged={() => void queryClient.invalidateQueries({ queryKey: ['gift-cards'] })}
          giftCardId={detailCardId}
          readOnly={!canManageGiftCards}
        />
      )}

      {/* ── Void confirm dialog ── */}
      {voidConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Void Gift Card</h3>
            <p className="text-sm text-slate-600 mb-6">
              Are you sure you want to void gift card{' '}
              <span className="font-mono font-semibold">{voidTarget?.serialNumber}</span>?
              This will set the balance to zero and mark the card as inactive. This action cannot
              be undone.
            </p>
            {voidMutation.isError && (
              <p className="text-sm text-red-600 mb-4">
                Failed to void gift card. Please try again.
              </p>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setVoidConfirmOpen(false); setVoidTarget(null); }}
                disabled={voidMutation.isPending}
                className="border border-slate-300 hover:bg-slate-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleVoidConfirm}
                disabled={voidMutation.isPending}
                className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {voidMutation.isPending ? 'Voiding...' : 'Void Gift Card'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
